import { Link } from 'react-router-dom'
import LegalPageLayout from '../components/legal/LegalPageLayout'

const LAST_UPDATED = 'December 26, 2024'

const SECTIONS = [
  { id: 'summary', title: 'Summary' },
  { id: 'acceptance', title: 'Acceptance of Terms' },
  { id: 'service', title: 'Description of Service' },
  { id: 'account', title: 'Your Account' },
  { id: 'data-privacy', title: 'Your Data & Privacy' },
  { id: 'acceptable-use', title: 'Acceptable Use' },
  { id: 'intellectual-property', title: 'Intellectual Property' },
  { id: 'disclaimer', title: 'Disclaimer of Warranties' },
  { id: 'liability', title: 'Limitation of Liability' },
  { id: 'termination', title: 'Termination' },
  { id: 'changes', title: 'Changes to Terms' },
  { id: 'governing-law', title: 'Governing Law' },
  { id: 'contact', title: 'Contact Us' }
]

export default function Terms() {
  return (
    <LegalPageLayout
      title="Terms of Service"
      lastUpdated={LAST_UPDATED}
      sections={SECTIONS}
    >
      {/* Summary */}
      <section id="summary">
        <div className="not-prose mb-6 p-6 bg-white rounded-lg border border-neutral-200 shadow-sm">
          <h2 className="text-base font-semibold text-neutral-900 mb-4">
            Summary
          </h2>
          <div className="space-y-3 text-[15px] text-neutral-600">
            <p>
              <span className="font-medium text-neutral-800">The service:</span>{' '}
              Taskrdo is a personal productivity app that helps you manage tasks, goals, and get things done.
            </p>
            <p>
              <span className="font-medium text-neutral-800">Your data:</span>{' '}
              Everything you create belongs to you. Export or delete it anytime.
            </p>
            <p>
              <span className="font-medium text-neutral-800">No guarantees:</span>{' '}
              The service is provided as-is. We work to keep it reliable, but can't promise 100% uptime.
            </p>
            <p>
              <span className="font-medium text-neutral-800">Use responsibly:</span>{' '}
              Don't abuse the service or use it to harm others.
            </p>
          </div>
        </div>
      </section>

      {/* Acceptance of Terms */}
      <section id="acceptance">
        <h2>Acceptance of Terms</h2>
        <p>
          By creating an account or using Taskrdo, you agree to be bound by these Terms of Service.
          If you don't agree with any part of these terms, please don't use our service.
        </p>
        <p>
          These terms constitute a binding agreement between you and Daniel Knight, the operator
          of Taskrdo.
        </p>
      </section>

      {/* Description of Service */}
      <section id="service">
        <h2>Description of Service</h2>
        <p>
          Taskrdo is a <strong>personal</strong> productivity application designed for individuals
          who want to take control of their own goals and get things done. Unlike corporate task
          management tools built for managers and teams, Taskrdo is built for <em>you</em>—to help
          you focus on what matters most in your life and work.
        </p>
        <p>
          Taskrdo helps you:
        </p>
        <ul>
          <li>Manage tasks and to-do lists on your own terms</li>
          <li>Set and track annual, quarterly, and weekly goals that matter to you</li>
          <li>Organize work with projects and labels in a way that makes sense for your workflow</li>
          <li>Visualize your personal productivity with analytics and insights</li>
          <li>Stay focused with daily planning and reflection tools</li>
          <li>Build sustainable habits through intentional goal-setting</li>
        </ul>
        <p>
          This is your space to plan, prioritize, and execute—without the noise of team
          notifications or corporate productivity theater. Your productivity is personal,
          and Taskrdo respects that.
        </p>
        <p>
          <strong>Service changes:</strong> Features may change, be added, or removed as we continue
          to develop the product. We'll do our best to communicate significant changes that affect
          your experience.
        </p>
      </section>

      {/* Your Account */}
      <section id="account">
        <h2>Your Account</h2>
        <h3>Account Creation</h3>
        <p>
          To use Taskrdo, you must create an account with a valid email address. You must be at
          least 13 years old to use this service.
        </p>

        <h3>Account Security</h3>
        <p>
          You are responsible for:
        </p>
        <ul>
          <li>Keeping your password secure and confidential</li>
          <li>All activity that occurs under your account</li>
          <li>Notifying us immediately if you suspect unauthorized access</li>
        </ul>
        <p>
          We recommend enabling multi-factor authentication (MFA) for enhanced security.
        </p>

        <h3>Accurate Information</h3>
        <p>
          You agree to provide accurate and complete information when creating your account
          and to keep this information up to date.
        </p>
      </section>

      {/* Your Data & Privacy */}
      <section id="data-privacy">
        <h2>Your Data & Privacy</h2>
        <h3>You Own Your Data</h3>
        <p>
          Your tasks, goals, notes, and other content belong to you. We don't claim ownership
          of any content you create in Taskrdo.
        </p>

        <h3>Limited License</h3>
        <p>
          By using Taskrdo, you grant us a limited license to store, process, and display your
          content solely for the purpose of providing the service to you. This license ends
          when you delete your content or account.
        </p>

        <h3>Privacy</h3>
        <p>
          Our handling of your personal information is described in our{' '}
          <Link to="/privacy">Privacy Policy</Link>. Key points:
        </p>
        <ul>
          <li>We don't sell your data</li>
          <li>We don't use your content for advertising</li>
          <li>We don't share your data with third parties for marketing</li>
          <li>You can export or delete your data at any time</li>
        </ul>

        <h3>Data Portability</h3>
        <p>
          You have the right to request an export of your data in a portable format. Contact
          us at <a href="mailto:support@taskrdo.com">support@taskrdo.com</a> to request an export.
        </p>
      </section>

      {/* Acceptable Use */}
      <section id="acceptable-use">
        <h2>Acceptable Use</h2>
        <p>
          When using Taskrdo, you agree not to:
        </p>
        <ul>
          <li>Violate any laws or regulations</li>
          <li>Attempt to gain unauthorized access to our systems or other users' accounts</li>
          <li>Interfere with or disrupt the service or servers</li>
          <li>Upload malicious code, viruses, or harmful content</li>
          <li>Use the service for any illegal activities</li>
          <li>Attempt to reverse engineer, decompile, or extract source code</li>
          <li>Use automated systems (bots, scrapers) to access the service without permission</li>
          <li>Impersonate another person or entity</li>
        </ul>
        <p>
          <strong>Note:</strong> Since Taskrdo is a personal productivity tool without public
          content sharing, most of these restrictions are about protecting the service itself
          rather than other users.
        </p>
      </section>

      {/* Intellectual Property */}
      <section id="intellectual-property">
        <h2>Intellectual Property</h2>
        <h3>Our Property</h3>
        <p>
          Taskrdo, including its design, code, features, and branding (logo, name, visuals),
          is owned by Daniel Knight and protected by intellectual property laws. You may not
          copy, modify, distribute, or create derivative works based on our service without
          explicit permission.
        </p>

        <h3>Your Content</h3>
        <p>
          You retain all rights to the content you create in Taskrdo. We don't claim ownership
          of your tasks, goals, notes, or any other user-generated content.
        </p>

        <h3>Feedback</h3>
        <p>
          If you provide suggestions, ideas, or feedback about Taskrdo, you grant us the right
          to use that feedback without compensation or attribution. You're not obligated to
          provide feedback.
        </p>
      </section>

      {/* Disclaimer of Warranties */}
      <section id="disclaimer">
        <h2>Disclaimer of Warranties</h2>
        <div className="not-prose my-6 p-5 bg-neutral-100 rounded-lg">
          <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
            Legal Notice
          </p>
          <p className="text-sm text-neutral-700 leading-relaxed">
            Taskrdo is provided "as is" and "as available" without warranties of any kind,
            either express or implied, including but not limited to implied warranties of
            merchantability, fitness for a particular purpose, and non-infringement.
          </p>
        </div>
        <p>
          In plain language, this means:
        </p>
        <ul>
          <li>We work hard to keep Taskrdo reliable, but we can't promise it will always work perfectly</li>
          <li>The service may have bugs, downtime, or issues we haven't discovered yet</li>
          <li>We can't guarantee that Taskrdo will meet all your specific needs</li>
          <li>You use the service at your own discretion and risk</li>
        </ul>
        <p>
          <strong>Backup your important data:</strong> While we take data security seriously,
          we recommend keeping backups of critical information outside of any single service.
        </p>
      </section>

      {/* Limitation of Liability */}
      <section id="liability">
        <h2>Limitation of Liability</h2>
        <div className="not-prose my-6 p-5 bg-neutral-100 rounded-lg">
          <p className="text-xs font-medium text-neutral-500 uppercase tracking-wider mb-2">
            Legal Notice
          </p>
          <p className="text-sm text-neutral-700 leading-relaxed">
            To the maximum extent permitted by law, Daniel Knight shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages, or any loss of
            data, use, or profits, arising out of or related to your use of Taskrdo.
          </p>
        </div>
        <p>
          In plain language:
        </p>
        <ul>
          <li>We're not responsible for indirect damages (like lost business opportunities)</li>
          <li>We're not responsible for data loss (please keep backups)</li>
          <li>Our total liability is limited to the amount you've paid for the service</li>
        </ul>
        <p>
          This limitation applies to the fullest extent permitted by law. Some jurisdictions
          don't allow these limitations, so they may not apply to you.
        </p>
      </section>

      {/* Termination */}
      <section id="termination">
        <h2>Termination</h2>
        <h3>Your Right to Terminate</h3>
        <p>
          You can delete your account at any time through the app settings. When you delete
          your account:
        </p>
        <ul>
          <li>All your data will be permanently deleted within 30 days</li>
          <li>This action is irreversible</li>
          <li>You can export your data before deletion if desired</li>
        </ul>

        <h3>Our Right to Terminate</h3>
        <p>
          We may suspend or terminate your account if:
        </p>
        <ul>
          <li>You violate these Terms of Service</li>
          <li>You engage in abusive or harmful behavior</li>
          <li>We're required to do so by law</li>
          <li>We discontinue the service (with reasonable notice)</li>
        </ul>

        <h3>Effect of Termination</h3>
        <p>
          Upon termination, your right to use Taskrdo ends. Certain provisions of these terms
          survive termination, including intellectual property rights, disclaimers, and
          limitations of liability.
        </p>
      </section>

      {/* Changes to Terms */}
      <section id="changes">
        <h2>Changes to Terms</h2>
        <p>
          We may update these Terms of Service from time to time. When we make changes:
        </p>
        <ul>
          <li>We'll update the "Last updated" date at the top</li>
          <li>For significant changes, we'll notify you via email or in-app notification</li>
          <li>We'll give you reasonable time (typically 14-30 days) to review material changes</li>
          <li>Continued use after changes take effect means you accept the new terms</li>
        </ul>
        <p>
          If you disagree with updated terms, you may delete your account before the changes
          take effect.
        </p>
      </section>

      {/* Governing Law */}
      <section id="governing-law">
        <h2>Governing Law</h2>
        <p>
          These Terms of Service are governed by and construed in accordance with the laws of
          the State of Missouri, United States, without regard to conflict of law principles.
        </p>
        <p>
          Any disputes arising from these terms or your use of Taskrdo will be resolved in
          the courts of Missouri, and you consent to the jurisdiction of those courts.
        </p>

        <h3>Dispute Resolution</h3>
        <p>
          Before filing any claim, we encourage you to contact us at{' '}
          <a href="mailto:support@taskrdo.com">support@taskrdo.com</a> to resolve the issue
          informally. Most concerns can be resolved through direct communication.
        </p>
      </section>

      {/* Contact Us */}
      <section id="contact">
        <h2>Contact Us</h2>
        <p>
          If you have questions about these Terms of Service, please reach out:
        </p>
        <ul>
          <li>
            <strong>Email:</strong> <a href="mailto:support@taskrdo.com">support@taskrdo.com</a>
          </li>
        </ul>
        <p>
          We aim to respond to all inquiries within a reasonable timeframe.
        </p>
      </section>

      {/* General Provisions */}
      <div className="not-prose mt-14 p-6 bg-white rounded-lg border border-neutral-200">
        <h3 className="text-base font-semibold text-neutral-900 mb-4">General Provisions</h3>
        <ul className="space-y-3 text-[15px] text-neutral-600">
          <li>
            <span className="font-medium text-neutral-800">Entire Agreement:</span> These terms, along
            with our Privacy Policy, constitute the complete agreement between you and Taskrdo.
          </li>
          <li>
            <span className="font-medium text-neutral-800">Severability:</span> If any provision of
            these terms is found unenforceable, the remaining provisions continue in effect.
          </li>
          <li>
            <span className="font-medium text-neutral-800">No Waiver:</span> Our failure to enforce
            any right or provision doesn't constitute a waiver of that right.
          </li>
          <li>
            <span className="font-medium text-neutral-800">Assignment:</span> You may not assign your
            rights under these terms. We may assign our rights to a successor or affiliate.
          </li>
        </ul>
      </div>
    </LegalPageLayout>
  )
}
